import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronRight, RefreshCw, PartyPopper } from 'lucide-react';
import { PendingActionCategory, PendingAction } from '@/hooks/useClientPendingActions';

interface PendingActionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: PendingActionCategory[];
  totalCount: number;
  onRefresh: () => void;
  loading: boolean;
}

export const PendingActionsModal = ({
  open,
  onOpenChange,
  categories,
  totalCount,
  onRefresh,
  loading,
}: PendingActionsModalProps) => {
  const navigate = useNavigate();

  const handleActionClick = (action: PendingAction) => {
    onOpenChange(false);
    navigate(action.navigationUrl);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Pending Actions</DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={loading}
              className="h-8 w-8 p-0"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <DialogDescription>
            {totalCount > 0
              ? `You have ${totalCount} item${totalCount > 1 ? 's' : ''} requiring your attention`
              : 'No pending actions'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          {categories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <PartyPopper className="h-12 w-12 text-success mb-4" />
              <h3 className="text-lg font-semibold text-foreground">You're all caught up!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                No pending actions at this time.
              </p>
            </div>
          ) : (
            <div className="space-y-4 pr-4">
              {categories.map((category) => (
                <div key={category.type} className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <span>{category.icon}</span>
                    <span>{category.label}</span>
                    <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                      {category.count}
                    </span>
                  </div>
                  <div className="space-y-1 pl-6">
                    {category.actions.map((action) => (
                      <button
                        key={action.id}
                        onClick={() => handleActionClick(action)}
                        className="w-full flex items-center justify-between p-2 rounded-md text-left hover:bg-muted/50 transition-colors group"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {action.jobTitle}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {action.description}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground ml-2 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
